'use client';

import './input-field.scss';

import { type MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import emojiRegex from 'emoji-regex-xs';
import {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  FieldError,
  FieldValues,
  UseFormSetError,
  UseFormSetValue,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIntersection } from 'react-use';

import { Either } from '@sola/schema/dotcom/helpers.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { cn, removeCreditCardNumbers } from '../../utils/helpers';
import { removeIllegalChars } from '../../utils/patterns';
import {
  Field,
  FieldErrorIcon,
  FieldErrorMessage,
  FieldIcon,
  type FieldProps,
  FieldTooltipIcon,
  FieldWrapper,
  getErrorElId,
} from '../field/field';

export interface InputFieldWithLabel {
  label: string;
}
export interface InputFieldWithAriaLabel {
  'aria-label': React.ComponentPropsWithoutRef<'input'>['aria-label'];
}
export interface InputFieldWithoutLabel {
  'aria-labelledby': React.ComponentPropsWithoutRef<'input'>['aria-labelledby'];
}

// TODO: How do we want to extend native input props (applies to Field as well)?
// TODO: Will be breaking up cc logic into their own fields.
export type InputFieldProps = Partial<
  Omit<FieldProps, 'children' | 'min' | 'max' | 'valueAsDate'>
> &
  Either<
    InputFieldWithLabel,
    Either<InputFieldWithAriaLabel, InputFieldWithoutLabel>
  > & {
    inputRef?: React.MutableRefObject<HTMLInputElement>;
    maskOptions?: MaskitoOptions;
    inputClasses?: string;
    placeholder?: string;
    help?: string | ReactNode;
    /**
     * Allows overriding the default icon implementation with a custom element.
     */
    iconElement?: ReactNode;
    iconName?: IconNames;
    iconClasses?: string;
    iconLabel?: string;
    iconAction?: () => void;
    footer?: ReactNode;
    hint?: string;
    maxLength?: number;
    prefix?: ReactNode;
    // NOTE: not sure what we want to allow for vs creating custom components.
    type?: 'text' | 'email' | 'hidden' | 'password' | 'tel' | 'url' | 'number';
    inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
    step?: InputHTMLAttributes<HTMLInputElement>['step'];
    autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
    autoCapitalize?: 'off' | 'on' | 'words' | 'characters';
    // Safari-only
    autoCorrect?: InputHTMLAttributes<HTMLInputElement>['autoCorrect'];
    spellCheck?: InputHTMLAttributes<HTMLInputElement>['spellCheck'];
    onAutoFillStart?: () => void;
    onKeyUp?: InputHTMLAttributes<HTMLInputElement>['onKeyUp'];
    onKeyDown?: InputHTMLAttributes<HTMLInputElement>['onKeyDown'];
    onFocus?: InputHTMLAttributes<HTMLInputElement>['onFocus'];
    onVisible?: () => void;
    illegalCharsRegex?: RegExp;
    allowEmoji?: boolean;
    removeCardNumbers?: {
      message: string;
    };
    ignoreResetEmpty?: boolean;
    ignoreValidateEmpty?: boolean;
    preventTrim?: boolean;
  };

const handleCreditCardNumbers = ({
  name,
  setError,
  setValue,
  value,
  message,
}: InputFieldProps['removeCardNumbers'] & {
  name: string;
  setError?: UseFormSetError<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  value: string;
}) => {
  // We're not in a form context.
  if (!setValue || !setError) {
    return;
  }
  const cleanedValue = removeCreditCardNumbers(value);
  if (value === cleanedValue) {
    return;
  }
  setValue(name, '');
  setError(name, { type: 'manual', message });
};

const shouldTriggerMaxLength = (
  error: FieldError | undefined,
  fldName?: string,
  maxLength?: number,
  value?: string,
) => {
  if (!fldName || !maxLength || !value) {
    return;
  }

  return (
    (value.length > maxLength && error?.type !== 'maxLength') ||
    (error?.type === 'maxLength' && value.length <= maxLength)
  );
};

export const InputField: React.FC<InputFieldProps> = ({
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  help,
  inputRef,
  label,
  maskOptions,
  inputClasses,
  placeholder,
  footer,
  hint,
  maxLength = 255,
  iconElement,
  iconName,
  iconClasses,
  iconLabel,
  iconAction,
  prefix,
  type = 'text',
  inputMode,
  step,
  autoComplete,
  autoCapitalize,
  autoCorrect,
  spellCheck,
  removeCardNumbers,
  onFocus,
  onAutoFillStart,
  onVisible,
  onKeyDown,
  illegalCharsRegex,
  allowEmoji = false,
  ignoreResetEmpty = false,
  ignoreValidateEmpty = false,
  preventTrim = false,
  ...fieldProps
}) => {
  const { t } = useTranslation(['forms']);
  const { setError, setValue, trigger } = useFormContext() || {};
  const maskRef = useMaskito({ options: maskOptions });
  const internalRef = useRef<HTMLInputElement>(null);

  const intersection = useIntersection(
    internalRef as React.RefObject<HTMLElement>,
    {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    },
  );
  useEffect(() => {
    if (!onVisible) {
      return;
    }
    onVisible();
  }, [intersection]);

  const illegalRegex = useMemo(() => {
    return !illegalCharsRegex && !allowEmoji && type !== 'number'
      ? emojiRegex()
      : illegalCharsRegex;
  }, [illegalCharsRegex, allowEmoji, type]);

  const maxLengthValidation = {
    maxLength: {
      value: maxLength,
      message: t('forms:errors.fieldMaxLimit', {
        limit: maxLength,
      }),
    },
  };

  return (
    <Field
      {...(fieldProps as FieldProps)}
      controlRef={inputRef}
      {...(maxLength && maxLengthValidation)}
    >
      {({ controlProps: { onChange, onBlur, ...controlProps }, error }) => {
        const errorElId = getErrorElId(controlProps.id);
        const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
          if (onChange) {
            if (illegalRegex) {
              removeIllegalChars(
                e as React.ChangeEvent<HTMLInputElement>,
                illegalRegex,
              );
            }
            onChange(e as React.ChangeEvent<HTMLInputElement>);
          }
        };

        const handleWhitespaceTrim = (name: string, value: string) => {
          if (!setValue) {
            return;
          }

          /**
           * If the value is empty after trimming, reset the field unless ignored
           *
           * `else if` there is/are trailing or leading whitespace(s), trim the value
           * NOTE: 'email' type automagically trims whitespace from the value but does NOT update the field
           */
          if (!!value && value.trim().length === 0) {
            if (!ignoreResetEmpty) {
              setValue(name, '', { shouldValidate: !ignoreValidateEmpty });
            }
          } else if (/(^\s+|\s+$)/.test(value) || type === 'email') {
            /**

             */
            setValue(name, value.trim(), {
              shouldValidate: !ignoreValidateEmpty,
            });
          }
        };

        const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          if (onBlur) {
            onBlur(e);
          }

          const { value } = e.target;
          const { name } = fieldProps;
          if (!name) {
            console.warn(
              'InputField: `name` prop is required for logic in `onBlur` handler',
            );
            return;
          }

          // Handle credit card validation first if applicable
          if (!error && removeCardNumbers && value) {
            handleCreditCardNumbers({
              name,
              setError,
              setValue,
              value,
              ...removeCardNumbers,
            });
          }

          // Handle whitespace trimming unless explicitly prevented
          if (!preventTrim) {
            handleWhitespaceTrim(name, value);
          }
        };

        const handleOnKeyup = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (fieldProps.onKeyUp) {
            fieldProps.onKeyUp(e);
          }

          const { value } = e.currentTarget ?? {};
          const { name: fldName } = fieldProps;
          if (shouldTriggerMaxLength(error, fldName, maxLength, value)) {
            trigger(fldName);
          }

          if (!error || !removeCardNumbers) {
            return;
          }

          handleCreditCardNumbers({
            name: fieldProps.name as string,
            setError,
            setValue,
            value,
            ...removeCardNumbers,
          });
          // Set error...
        };

        return (
          <>
            {label && (
              <label className="form-label label" htmlFor={controlProps.id}>
                {label}
              </label>
            )}

            <FieldWrapper
              className={cn('InputField', {
                'InputField--help': help,
              })}
            >
              {prefix && (
                <div className="InputField__prefix" aria-hidden>
                  {prefix}
                </div>
              )}
              <input
                className={cn('form-control', {
                  [`${inputClasses}`]: !!inputClasses,
                })}
                type={type || 'text'}
                aria-labelledby={ariaLabelledBy}
                aria-label={ariaLabel}
                aria-invalid={!!error}
                placeholder={placeholder}
                inputMode={inputMode}
                step={step}
                aria-errormessage={errorElId}
                autoCapitalize={autoCapitalize}
                autoComplete={autoComplete}
                autoCorrect={autoCorrect}
                spellCheck={spellCheck}
                onClick={() => {
                  trackClick(
                    label || 'Unlabeled Input Field',
                    undefined,
                    'form-control',
                  );
                }}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                {...controlProps}
                {...(maxLength && {
                  maxLength: maxLength + 1,
                })}
                {...((onChange || maskOptions) && {
                  onInput: handleChange,
                })}
                {...((onBlur || removeCardNumbers) && {
                  onBlur: handleOnBlur,
                })}
                {...((removeCardNumbers || maxLength) && {
                  onKeyUp: handleOnKeyup,
                })}
                ref={(node) => {
                  if (!node) {
                    return;
                  }

                  // eslint-disable-next-line no-param-reassign
                  internalRef.current = node;

                  if (maskOptions) {
                    maskRef(node);
                  }

                  if (controlProps.ref) {
                    controlProps.ref(node);
                  } else if (inputRef) {
                    // eslint-disable-next-line no-param-reassign
                    inputRef.current = node;
                  }
                }}
              />

              <FieldTooltipIcon help={help} />
              <FieldErrorIcon error={error} />
              {iconElement || (
                <FieldIcon
                  iconName={iconName}
                  className={iconClasses}
                  label={iconLabel}
                  action={iconAction}
                />
              )}
            </FieldWrapper>

            {hint && (
              <label
                className="form-label--below label label-mild"
                htmlFor={controlProps.id}
              >
                {hint}
              </label>
            )}

            {/* Added a footer here to give a Radix PopoverAnchor something to attach to,
            but it could be improved by breaking these different sections/nodes out into
            compositional components */}
            {footer}

            <FieldErrorMessage error={error} id={errorElId} />
          </>
        );
      }}
    </Field>
  );
};
